import { JOB_LIMITS } from "../consts";
import type { CareersJob, CareersJobStep } from "../types";

/**
 * One lane of the pipeline board. With one job a lane is one of its steps. With several jobs ("All jobs")
 * a lane is a step NAME: every job that has a step by that name (case-insensitive) sends its candidates
 * there.
 */
export type BoardColumn = {
	/** The id of the first step that brought the name onto the board, so a one-job board keeps its step ids. */
	id: string;
	/** As that first step spells it. */
	name: string;
	/** Each job that has this step → its own step. A card can only land in a lane its job is in. */
	stepByJob: Map<string, CareersJobStep>;
	/** Any job marks its step by this name as the hired step — so a step added here for another job must be its hired step too. */
	isHired: boolean;
};

/** All the merge reads of a job - so the table's step filter can order its options the same way. */
type BoardJob = Pick<CareersJob, "id" | "steps">;

/** Step names match the way the backend's uniqueness rule does: trimmed, case-insensitive. */
export const stepNameKey = (name: string): string => name.trim().toLowerCase();

type Lane = BoardColumn & {
	/** When the name was first met: jobs in list order, each job's steps in position order. */
	firstSeen: number;
	/** Names that come right after this one in some job. */
	next: Set<string>;
	/** How many distinct names come right before this one in some job, and are not placed yet. */
	waitingOn: number;
};

/** Every step name across `jobs`, as a lane, with each job's "this step comes right after that one" links. */
const collectLanes = (jobs: BoardJob[]): Map<string, Lane> => {
	const lanes = new Map<string, Lane>();
	for (const job of jobs) {
		let previous: Lane | null = null;
		for (const step of [...job.steps].sort((a, b) => a.position - b.position)) {
			const key = stepNameKey(step.name);
			let lane = lanes.get(key);
			if (lane === undefined) {
				lane = { id: step.id, name: step.name, stepByJob: new Map(), firstSeen: lanes.size, isHired: false, next: new Set(), waitingOn: 0 };
				lanes.set(key, lane);
			}
			lane.stepByJob.set(job.id, step);
			lane.isHired ||= step.is_hired_step;
			if (previous !== null && previous !== lane && !previous.next.has(key)) {
				previous.next.add(key);
				lane.waitingOn += 1;
			}
			previous = lane;
		}
	}
	return lanes;
};

/**
 * Place every lane after every lane that some job puts before it (a topological order of the jobs'
 * "right after" links). Of the lanes ready to place, the one met FIRST goes next - so the first job keeps
 * its whole run together and a later job's own steps slot in where its order needs them. Two jobs that
 * disagree (A has X before Y, B has Y before X) leave no lane ready; then the earliest-met unplaced lane
 * goes next, which keeps the earlier job's order and gives way on the later one's.
 */
const mergeLanes = (lanes: Map<string, Lane>): Lane[] => {
	const unplaced = new Set(lanes.keys());
	const placed: Lane[] = [];
	const lane = (key: string): Lane => {
		const found = lanes.get(key);
		if (found === undefined) throw new Error(`Board lane ${key} is missing`);
		return found;
	};
	const earliest = (keys: string[]): string => keys.reduce((best, key) => (lane(key).firstSeen < lane(best).firstSeen ? key : best));

	while (unplaced.size > 0) {
		const keys = [...unplaced];
		const ready = keys.filter((key) => lane(key).waitingOn === 0);
		const key = earliest(ready.length > 0 ? ready : keys);
		unplaced.delete(key);
		const current = lane(key);
		placed.push(current);
		for (const nextKey of current.next) lane(nextKey).waitingOn -= 1;
	}
	return placed;
};

/**
 * The board's lanes for `jobs`: the union of their step names, merged so that NO job's own step order is
 * broken where the jobs agree (`mergeLanes`), and - with several jobs - every lane holding a hired step
 * last, so "Hired" never sits in front of another job's later steps. With one job this is exactly its
 * steps in position order, under their own ids (its hired step stays where the job put it).
 *
 * dev [Review, Phone Interview, Site Interview, Second Site interview, interview with Ari, Offer, Hired] +
 * product [Review, Interview, Offer, Hired] gives Review, Phone Interview, Site Interview, Second Site
 * interview, interview with Ari, Interview, Offer, Hired: after Review both Phone Interview (dev) and
 * Interview (product) are ready, and dev's was met first, so dev's run goes on until Offer, which needs
 * product's Interview first.
 */
export const boardColumns = (jobs: BoardJob[]): BoardColumn[] => {
	const merged = mergeLanes(collectLanes(jobs));
	const ordered = jobs.length > 1 ? [...merged.filter((lane) => !lane.isHired), ...merged.filter((lane) => lane.isHired)] : merged;
	return ordered.map(({ id, name, stepByJob, isHired }) => ({ id, name, stepByJob, isHired }));
};

/** Every step on the board → the lane it sits in. */
export const columnIdByStepId = (columns: BoardColumn[]): Map<string, string> =>
	new Map(columns.flatMap((column) => [...column.stepByJob.values()].map((step) => [step.id, column.id] as const)));

/**
 * Where a new step for `columnId`'s lane goes in `job`: after every step of the job that the board shows
 * before that lane. The lanes keep every job's own order, so those steps are the start of the job's list
 * and the new step lands where it reads on the board (dev's "Phone Interview" goes after product's Review
 * and before its Offer).
 */
export const laneInsertIndex = (columns: BoardColumn[], jobId: string, columnId: string): number => {
	const laneIndex = columns.findIndex((column) => column.id === columnId);
	if (laneIndex === -1) throw new Error(`Board lane ${columnId} is missing`);
	return columns.slice(0, laneIndex).filter((column) => column.stepByJob.has(jobId)).length;
};

/**
 * Whether a card of `job` may be dropped on `column`. In a lane of its own job's, always. In a lane the job has no
 * step for, the drop adds that step to the job (`AddStepDialog`) — so not when the job is at `JOB_LIMITS.MAX_STEPS`,
 * and not in a hired lane when the job already has a hired step under another name: the added step would have to be
 * the job's hired step (an ordinary one would put the candidate in "Hired" without hiring them), and a job has at
 * most one (400 `careers.job.multiple_hired_steps`). That candidate is hired through their own job's hired lane.
 */
export const canDropInLane = (column: BoardColumn, job: BoardJob): boolean => {
	if (column.stepByJob.has(job.id)) return true;
	if (column.isHired && job.steps.some((step) => step.is_hired_step)) return false;
	return job.steps.length < JOB_LIMITS.MAX_STEPS;
};
